import { AuditItem } from '@internal-audit/components/AuditItem';
import type { GraphQLAuditItem } from '@internal-audit/domain/graphql-types';
import { Text } from '@soteria/design-system-ve';

import * as styles from './AuditItemSection.css';

interface AuditItemsListProps {
  items: GraphQLAuditItem[];
  decisionsMap: Map<string, any[]>; // eslint-disable-line @typescript-eslint/no-explicit-any
  selectedItemId?: string;
  panelLoading?: boolean;
  onItemSelect?: (item: GraphQLAuditItem) => void;
  onDecisionSelect: (decisionId: string) => void;
  onDecisionSubmit: (itemId: string, hasDecision: boolean) => void;
  onSkipToggle: (itemId: string, isSkipped: boolean) => void;
  isFiltered: boolean;
  isSessionCompleted?: boolean;
  onContext?: (item: GraphQLAuditItem) => void;
}

/**
 * Empty state component for when no items are available
 */
function EmptyState({ isFiltered }: { isFiltered: boolean }) {
  return (
    <div className={styles.emptyState}>
      <Text>{isFiltered ? 'No audit items match the current filters. Try adjusting your filter criteria.' : 'No audit items available for this session.'}</Text>
    </div>
  );
}

/**
 * List of audit items with empty state handling
 */
export function AuditItemsList({
  items,
  decisionsMap,
  selectedItemId,
  panelLoading = false,
  onItemSelect,
  onDecisionSelect,
  onDecisionSubmit,
  onSkipToggle,
  isFiltered,
  isSessionCompleted = false,
  onContext,
}: AuditItemsListProps) {
  // Show empty state if no items
  if (items.length === 0) {
    return <EmptyState isFiltered={isFiltered} />;
  }

  // Render items list
  return (
    <div className={styles.auditItemsList}>
      {items.map(item => (
        <AuditItem
          key={item.id}
          decisions={decisionsMap.get(item.id) || []}
          dialogLoading={selectedItemId === item.id ? panelLoading : false}
          isSelected={selectedItemId === item.id}
          isSessionCompleted={isSessionCompleted}
          item={item}
          onContext={onContext}
          onDecisionSelect={onDecisionSelect}
          onDecisionSubmit={onDecisionSubmit}
          onItemSelect={onItemSelect}
          onSkipToggle={onSkipToggle}
        />
      ))}
    </div>
  );
}
